import { Components } from '@/api/generated/api';
import { BillingMode } from '@/api/generated/api.enum';
import type { TTranslate } from '@/interfaces';
import {
  BILLING_STATUS_COLOR_MUTED,
  BILLING_STATUS_COLOR_PRIMARY,
  BILLING_STATUS_COLOR_SUCCESS,
  BILLING_STATUS_COLOR_WARNING,
  type BillingInterval,
  type BillingInvoiceStatusPresentation,
} from './types';

const SUBSCRIPTION_STATUS_BADGE: Record<
  Components.Schemas.SubscriptionStatus,
  Omit<BillingInvoiceStatusPresentation, 'label'>
> = {
  active: { color: BILLING_STATUS_COLOR_SUCCESS, theme: 'fill' },
  trialing: { color: BILLING_STATUS_COLOR_PRIMARY, theme: 'outline' },
  past_due: { color: BILLING_STATUS_COLOR_WARNING, theme: 'fill' },
  unpaid: { color: BILLING_STATUS_COLOR_WARNING, theme: 'fill' },
  incomplete: { color: BILLING_STATUS_COLOR_WARNING, theme: 'outline' },
  paused: { color: BILLING_STATUS_COLOR_MUTED, theme: 'outline' },
  canceled: { color: BILLING_STATUS_COLOR_MUTED, theme: 'transparent' },
  incomplete_expired: { color: BILLING_STATUS_COLOR_MUTED, theme: 'transparent' },
};

export function getSubscriptionStatusPresentation(
  subscription: Pick<Components.Schemas.ProviderTenantSubscriptionDto, 'status' | 'cancelAtPeriodEnd' | 'endedAt'>,
  t: TTranslate,
): BillingInvoiceStatusPresentation {
  // Stripe keeps a cancelled subscription active until the period ends; the tenant list already calls it cancelled.
  if (subscription.cancelAtPeriodEnd && !subscription.endedAt) {
    return {
      color: BILLING_STATUS_COLOR_WARNING,
      label: t('billing.subscription.status.cancelScheduled'),
      theme: 'outline',
    };
  }
  return {
    ...SUBSCRIPTION_STATUS_BADGE[subscription.status],
    label: t(`billing.subscription.status.${subscription.status}`),
  };
}

export function getBillingIntervalLabel(billingInterval: BillingInterval | null, t: TTranslate): string {
  if (billingInterval === 'month') return t('billing.intervalToggle.monthlyShort');
  if (billingInterval === 'year') return t('billing.intervalToggle.yearlyShort');
  return t('billing.planSwitchConfirm.unknownInterval');
}

export function getBillingModeLabel(billingMode: Components.Schemas.BillingMode, t: TTranslate): string {
  if (billingMode === BillingMode.billedExternally) return t('tenants.billing.billedExternally');
  return t('tenants.billing.billedByTenant');
}
